// Passkey (WebAuthn) на клиенте — этап 16.
//
// Сервер отдаёт options в JSON-форме (base64url в бинарных полях) — их разбирает
// PublicKeyCredential.parseCreationOptionsFromJSON / parseRequestOptionsFromJSON, а
// ответ аутентификатора сериализует credential.toJSON(). На iOS 17+ и в свежем
// Chrome это есть; ниже — ручной фолбэк на те же преобразования, чтобы не тащить
// библиотеку. Логика входа/регистрации живёт здесь, чтобы экран входа и настройки
// не дублировали одно и то же.
import { api } from './api.js';

export function isPasskeySupported() {
  try {
    return typeof window !== 'undefined'
      && typeof window.PublicKeyCredential === 'function'
      && typeof navigator?.credentials?.create === 'function'
      && window.isSecureContext !== false;
  } catch {
    return false;
  }
}

// Есть ли на этом устройстве платформенный аутентификатор (Face ID / Touch ID).
export async function isPlatformAuthenticatorAvailable() {
  if (!isPasskeySupported()) {
    return false;
  }
  try {
    const check = window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable;
    return typeof check === 'function' ? Boolean(await check()) : true;
  } catch {
    return false;
  }
}

function base64urlToBuffer(value) {
  const padded = String(value).replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (value.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function parseCreationOptions(json) {
  const native = window.PublicKeyCredential?.parseCreationOptionsFromJSON;
  if (typeof native === 'function') {
    return native.call(window.PublicKeyCredential, json);
  }
  return {
    ...json,
    challenge: base64urlToBuffer(json.challenge),
    user: { ...json.user, id: base64urlToBuffer(json.user.id) },
    excludeCredentials: (json.excludeCredentials || []).map((c) => ({ ...c, id: base64urlToBuffer(c.id) })),
  };
}

function parseRequestOptions(json) {
  const native = window.PublicKeyCredential?.parseRequestOptionsFromJSON;
  if (typeof native === 'function') {
    return native.call(window.PublicKeyCredential, json);
  }
  return {
    ...json,
    challenge: base64urlToBuffer(json.challenge),
    allowCredentials: (json.allowCredentials || []).map((c) => ({ ...c, id: base64urlToBuffer(c.id) })),
  };
}

function credentialToJSON(credential) {
  if (typeof credential.toJSON === 'function') {
    return credential.toJSON();
  }
  const response = credential.response;
  const out = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults?.() || {},
    response: { clientDataJSON: bufferToBase64url(response.clientDataJSON) },
  };
  if (response.attestationObject) {
    out.response.attestationObject = bufferToBase64url(response.attestationObject);
    out.response.transports = response.getTransports?.() || [];
  } else {
    out.response.authenticatorData = bufferToBase64url(response.authenticatorData);
    out.response.signature = bufferToBase64url(response.signature);
    out.response.userHandle = response.userHandle ? bufferToBase64url(response.userHandle) : null;
  }
  return out;
}

// Отмена пользователем (закрыл системную шторку) — не ошибка, а «передумал».
export function isUserCancel(error) {
  return error?.name === 'NotAllowedError' || error?.name === 'AbortError';
}

export async function registerPasskey(label = '') {
  const { options } = await api.getPasskeyRegisterOptions();
  const credential = await navigator.credentials.create({ publicKey: parseCreationOptions(options) });
  if (!credential) {
    throw new Error('no credential');
  }
  return api.verifyPasskeyRegistration({ credential: credentialToJSON(credential), label });
}

export async function loginWithPasskey() {
  const { options } = await api.getPasskeyLoginOptions();
  const credential = await navigator.credentials.get({ publicKey: parseRequestOptions(options) });
  if (!credential) {
    throw new Error('no credential');
  }
  return api.verifyPasskeyLogin({ credential: credentialToJSON(credential) });
}
